import { FC, useEffect, useState } from "react";
import CustomSelect from "../../../components/forms/CustomSelect";
import RequiredInput from "../../../components/forms/InputHOC";
import { toast } from "sonner";
import { useFormik } from "formik";
import useHandleErrors from "../../../hooks/useHandleErrors";
import useCompanies from "../../../hooks/api-calls/useCompanies";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import * as yup from "yup";
import { useAtom } from "jotai";
import { formsSubmitAtom } from "../../../atom/atom";
import { Headquarter } from "../../../type/entities/headquarter-type";
import { HeadquarterService } from "../../../services/headquarters/headquarterService";
import useProviders from "../../../hooks/api-calls/useProviders";
import useRoutes from "../../../hooks/api-calls/useRoutes";
import { ProviderService } from "../../../services/providers/providerService";
import { Input, Switch, Button, Textarea } from "rizzui";
import { DatePicker } from "../../../components/forms/DatePicker";
import moment from "moment";
import { es } from "date-fns/locale";

interface HeadquarterFormProps {
    setOpenModal: any;
    refetch: () => void;
    headquarter?: Headquarter | null;
    formRef?: any;
    providerSelected?: string | null;
}

interface HighSeasonDate {
    dateFrom: string;
    dateTo: string;
    coordinates: string;
}

// Helper to normalize potential JSON string into an array for highSeasonDates
const normalizeHighSeasonDates = (dates: any): HighSeasonDate[] => {
    if (!dates) return [];
    if (typeof dates === 'string') {
        try {
            const parsed = JSON.parse(dates);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            console.error('Error parsing highSeasonDates JSON string:', e);
            return [];
        }
    }
    return Array.isArray(dates) ? dates : [];
};

const schema = yup.object({
    name                : yup.string().required('El nombre es obligatorio'),
    providerId          : yup.string().required('El proveedor es obligatorio'),
    address             : yup.string().required('La dirección es obligatoria'),
    city                : yup.string().required('La ciudad es obligatoria'),
    province            : yup.string().nullable(),
    pickBetweenDays     : yup.number().required('El número de días es obligatorio'),
    highSeason          : yup.boolean(),
    highSeasonDateFrom: yup.string().nullable(),
    highSeasonDateTo: yup.string().nullable(),
    coordinates: yup.string().required('Las coordenadas son obligatorias debe tener el formato (latitud, longitud), ejemplo: (12.30, -100.15)')
        .test('coordinates-format', 'Las coordenadas deben tener el formato (latitud, longitud), ejemplo: (12.30, -100.15)', function(value) {
            if (!value || value.trim() === '') return true; // Permitir coordenadas vacías
            return /^\(-?\d+\.?\d*,\s*-?\d+\.?\d*\)$/.test(value);
        }),
    neighbourhood: yup.string().nullable(),
    zipCode: yup.string().nullable(),
    contactName: yup.string().nullable(),
    mobilePhone: yup.string().nullable(),
    phone: yup.string().nullable(),
    email: yup.string().email('Email inválido').nullable(),
    startService: yup.date().nullable(),
    endService: yup.date().nullable(),
    gratification: yup.string().nullable(),
    comments: yup.string().nullable(),
    schedules: yup.string().nullable(),
    dayOff: yup.string().nullable(),
    active: yup.boolean(),
    routeId: yup.string().nullable(),
    highSeasonDates: yup
        .array()
        .transform((value, originalValue) => {
            // Ensure the value entering validation is always an array
            if (Array.isArray(originalValue)) return originalValue;
            if (typeof originalValue === 'string') {
                try {
                    const parsed = JSON.parse(originalValue);
                    return Array.isArray(parsed) ? parsed : [];
                } catch (e) {
                    console.error('Error parsing highSeasonDates in schema transform:', e);
                    return [];
                }
            }
            if (originalValue == null) return [];
            return Array.isArray(value) ? value : [];
        })
        .of(
        yup.object().shape({
            dateFrom: yup.string()
                .required('La fecha de inicio es obligatoria')
                .test('date-format', 'Formato de fecha inválido', function(value) {
                    return moment(value, 'YYYY-MM-DD', true).isValid();
                }),
            dateTo: yup.string()
                .required('La fecha de fin es obligatoria')
                .test('date-format', 'Formato de fecha inválido', function(value) {
                    return moment(value, 'YYYY-MM-DD', true).isValid();
                })
                .test('date-after', 'La fecha de fin debe ser posterior a la fecha de inicio', function(value) {
                    const { dateFrom } = this.parent;
                    if (!dateFrom || !value) return true;
                    return moment(value).isAfter(moment(dateFrom));
                })
        })
    )
    .test('required-when-high-season', 'Debe agregar al menos una fecha de temporada alta', function(value) {
        const { highSeason } = this.parent;
        if (highSeason && (!value || value.length === 0)) {
            return false;
        }
        return true;
    })
})

const superAdminSchema = schema.concat(yup.object({
    company: yup.string().required('La compañía es obligatoria'),
}))

export const RETRIBUTION_TYPES = [
    {
        label: 'Productos',
        value: 'product',
    },
    {
        label: 'Servicios',
        value: 'service',
    },
    {
        label: 'Dinero',
        value: 'money',
    },
    {
        label: 'Mixto',
        value: 'mixed',
    },
]

const HeadquarterForm:FC<HeadquarterFormProps> = ({ setOpenModal, refetch, headquarter, formRef, providerSelected = null }) => {

    // STATES

    const service = new HeadquarterService();
    const isEdit  = headquarter !== null;
    const [isSearched, setIsSearched] = useState<boolean>(false);

    // HOOKS
    
    const [, setIsSubmitting]   = useAtom(formsSubmitAtom);
    const { handleErrors }      = useHandleErrors();
    const { getCompaniesList }  = useCompanies();
    const { getProvidersList }  = useProviders();
    const { fetchRoutes, getRoutesList } = useRoutes();
    const { userCan }           = usePrivilege();

    // FORMIK

    // Pre-normalize highSeasonDates to avoid validation errors when editing
    const normalizedHighSeasonDates = normalizeHighSeasonDates(headquarter?.highSeasonDates);

    const formik = useFormik({
        initialValues: {
            id                 : headquarter?.id ?? '',
            name               : headquarter?.name ?? '',
            providerId         : providerSelected ? providerSelected : headquarter?.provider?.id ?? '',
            address            : headquarter?.address ?? '',
            city               : headquarter?.city ?? '',
            province           : headquarter?.province ?? '',
            drumsNumber        : headquarter?.drumsNumber ?? 0,
            pickBetweenDays    : headquarter?.pickBetweenDays ?? 0,
            highSeason         : headquarter?.highSeason ?? (normalizedHighSeasonDates && normalizedHighSeasonDates.length > 0) ?? false,
            highSeasonDateFrom : headquarter?.highSeasonDateFrom?.date ? new Date(headquarter.highSeasonDateFrom.date) : (headquarter?.highSeasonDateFrom ? new Date(headquarter.highSeasonDateFrom) : null),
            highSeasonDateTo   : headquarter?.highSeasonDateTo?.date ? new Date(headquarter.highSeasonDateTo.date) : (headquarter?.highSeasonDateTo ? new Date(headquarter.highSeasonDateTo) : null),
            coordinates        : headquarter?.coordinates ?? null,
            dayOff             : headquarter?.dayOff ?? null,
            pickupSchedule     : headquarter?.pickupSchedule ?? null,
            company            : headquarter?.company?.id ?? '',
            neighbourhood      : headquarter?.neighbourhood ?? '',
            zipCode            : headquarter?.zipCode ?? '',
            contactName        : headquarter?.contactName ?? '',
            mobilePhone        : headquarter?.mobilePhone ?? '',
            phone              : headquarter?.phone ?? '',
            email              : headquarter?.email ?? '',
            startService       : headquarter?.startService?.date ? new Date(headquarter.startService.date) : (headquarter?.startService ? new Date(headquarter.startService) : null),
            endService         : headquarter?.endService?.date ? new Date(headquarter.endService.date) : (headquarter?.endService ? new Date(headquarter.endService) : null),
            gratification      : headquarter?.gratification ?? '',
            comments           : headquarter?.comments ?? '',
            schedules          : headquarter?.schedules ?? '',
            active             : headquarter?.active ?? true,
            highSeasonDates    : normalizedHighSeasonDates,
            routeId            : headquarter?.routeId ?? '',
        },
        validationSchema: userCan('list', 'company') ? superAdminSchema : schema,
        validateOnBlur: false,
        onSubmit: values => { 
            if(!isEdit) {
                handleCreate(values)
            } else {
                handleEdit(values)
            }
        },
    });

    // FUNCTIONS

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES AÑADIR UNA NUEVA FECHA DE TEMPORADA ALTA
     * @EN ADD A NEW HIGH SEASON DATE
     */
    //-------------------------------------------------------------------------------------------------------------
    const addHighSeasonDate = () => {
        const newDate: HighSeasonDate = {
            dateFrom: '',
            dateTo: '',
            coordinates: ''
        };
        const currentDates = Array.isArray(formik.values.highSeasonDates) ? formik.values.highSeasonDates : [];
        formik.setFieldValue('highSeasonDates', [...currentDates, newDate]);
    };

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINAR UNA FECHA DE TEMPORADA ALTA
     * @EN REMOVE A HIGH SEASON DATE
     */
    //-------------------------------------------------------------------------------------------------------------
    const removeHighSeasonDate = (index: number) => {
        const currentDates = Array.isArray(formik.values.highSeasonDates) ? formik.values.highSeasonDates : [];
        const updatedDates = currentDates.filter((_, i) => i !== index);
        formik.setFieldValue('highSeasonDates', updatedDates);
    };

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES ACTUALIZAR UNA FECHA DE TEMPORADA ALTA
     * @EN UPDATE A HIGH SEASON DATE
     */
    //-------------------------------------------------------------------------------------------------------------
    const updateHighSeasonDate = (index: number, field: keyof HighSeasonDate, value: string) => {
        const currentDates = Array.isArray(formik.values.highSeasonDates) ? [...formik.values.highSeasonDates] : [];
        if (currentDates[index]) {
            currentDates[index] = {
                ...currentDates[index],
                [field]: value
            };
            formik.setFieldValue('highSeasonDates', currentDates);
        }
    };

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES CREAR UNA NUEVA SUCURSAL
     * @EN CREATE A NEW HEADQUARTER
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleCreate = async (values: any) => {
        try {
            // Si no está activada la temporada alta, asegurar array vacío
            if (!values.highSeason) {
                values.highSeasonDates = [];
            }
            // Formatear fechas del array de temporada alta
            if (values.highSeasonDates && values.highSeasonDates.length > 0) {
                values.highSeasonDates = values.highSeasonDates.map((date: HighSeasonDate) => ({
                    dateFrom: moment(date.dateFrom).format('YYYY-MM-DD'),
                    dateTo: moment(date.dateTo).format('YYYY-MM-DD'),
                    coordinates: date.coordinates
                }));
            }

            // Mantener compatibilidad con campos antiguos (ocultos)
            values.highSeasonDateFrom = values.highSeasonDateFrom ? moment(values.highSeasonDateFrom).format('YYYY-MM-DD') : null;
            values.highSeasonDateTo = values.highSeasonDateTo ? moment(values.highSeasonDateTo).format('YYYY-MM-DD') : null;
            
            const response = await (await service.createHeadquarter(values)).getResponseData();
            if (response.success) {
                refetch();
                setOpenModal(false);
                formik.resetForm();
                toast.success('Sede creada correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-----------------------------------------------------------------------------------------------------------------
    

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES EDITAR UNA SUCURSAL
     * @EN EDIT A HEADQUARTER
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleEdit = async (values: any) => {
        try {
            // Si no está activada la temporada alta, asegurar array vacío
            if (!values.highSeason) {
                values.highSeasonDates = [];
            }
            // Formatear fechas del array de temporada alta
            if (values.highSeasonDates && values.highSeasonDates.length > 0) {
                values.highSeasonDates = values.highSeasonDates.map((date: HighSeasonDate) => ({
                    dateFrom: moment(date.dateFrom).format('YYYY-MM-DD'),
                    dateTo: moment(date.dateTo).format('YYYY-MM-DD'),
                    coordinates: date.coordinates
                }));
            }

            // Mantener compatibilidad con campos antiguos (ocultos)
            values.highSeasonDateFrom = values.highSeasonDateFrom ? moment(values.highSeasonDateFrom).format('YYYY-MM-DD') : null;
            values.highSeasonDateTo = values.highSeasonDateTo ? moment(values.highSeasonDateTo).format('YYYY-MM-DD') : null;
            
            const response = await (await service.editHeadquarter(values)).getResponseData();
            if (response.success) {
                refetch();
                setOpenModal(false);
                formik.resetForm();
                toast.success('Sede editada correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    // USE EFFECT

    useEffect(() => {
        setIsSubmitting(formik.isSubmitting);
    }, [formik.isSubmitting, setIsSubmitting]);

    useEffect(() => {
        if (!formik.values.company && getCompaniesList().length > 0 && !isSearched) {
            setIsSearched(true);
            let defaultCompany = getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU');
            formik.setFieldValue('company', defaultCompany?.value ?? '');
        }
    }, [getCompaniesList]);

    // Efecto para manejar la inicialización/actualización de highSeasonDates cuando se edita
    useEffect(() => {
        if (headquarter) {
            const parsedDates = normalizeHighSeasonDates(headquarter.highSeasonDates);
            formik.setFieldValue('highSeasonDates', parsedDates);
            if (parsedDates.length > 0) {
                formik.setFieldValue('highSeason', true);
            }
            formik.setFieldValue('routeId', headquarter.routeId ?? '');
        } else {
            formik.setFieldValue('highSeasonDates', []);
            formik.setFieldValue('routeId', '');
        }
    }, [headquarter]);

    useEffect(() => {
        fetchRoutes();
    }, []);

    useEffect(() => {
        let cancelled = false;
        const syncRouteFromApi = async () => {
            if (!isEdit || !headquarter?.id) {
                return;
            }
            if (headquarter.routeId !== undefined || headquarter.routeName !== undefined) {
                return;
            }
            try {
                const res = await (await service.getHeadquarterById(headquarter.id)).getResponseData();
                if (cancelled || !res?.success || !res.data) {
                    return;
                }
                formik.setFieldValue('routeId', res.data.routeId ?? '');
            } catch {
                /* list row sin detalle: no bloquear el formulario */
            }
        };
        syncRouteFromApi();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- sync routeId al abrir edición desde listado (fila sin routeId)
    }, [headquarter?.id, isEdit]);

    // RENDER

    return (
         <div className="@container">
            <form onSubmit={formik.handleSubmit} ref={formRef ? formRef : undefined}>
                <div className={'md:col-span-12'}>
                    <div className='md:grid md:grid-cols-12 gap-4'>

                        <RequiredInput
                            id='name'
                            label="Nombre"
                            className='md:col-span-12'
                            formik={formik}
                            error={formik.errors.name}
                        />

                        <RequiredInput
                            id='address'
                            label="Dirección"
                            className='md:col-span-12'
                            formik={formik}
                            error={formik.errors.address}
                        />

                        <Input
                            id='province'
                            label="Provincia"
                            className='md:col-span-6'
                            value={formik.values.province}
                            onChange={(e: any) => formik.setFieldValue('province', e.target.value)} 
                            error={formik.errors.province}
                        />

                        <RequiredInput
                            id='city'
                            label="Ciudad"
                            className='md:col-span-6'
                            formik={formik}
                            error={formik.errors.city}
                        />

                         <Input
                            id='neighbourhood'
                            label="Barrio"
                            className='md:col-span-6'
                            value={formik.values.neighbourhood || ''}
                            onChange={(e: any) => formik.setFieldValue('neighbourhood', e.target.value)}
                            error={formik.errors.neighbourhood}
                        />

                        <Input
                            id='zipCode'
                            label="Código Postal"
                            className='md:col-span-6'
                            value={formik.values.zipCode}
                            onChange={(e: any) => formik.setFieldValue('zipCode', e.target.value)}
                            error={formik.errors.zipCode}
                        />

                        {/*
                        <RequiredInput
                            id='drumsNumber'
                            label="Número de bidones"
                            className='md:col-span-6'
                            formik={formik}
                            error={formik.errors.drumsNumber}
                        />
                        */}

                        <RequiredInput
                            id='pickBetweenDays'
                            label="Días entre recogidas"
                            className='md:col-span-6'
                            formik={formik}
                            error={formik.errors.pickBetweenDays}
                        />

                        <CustomSelect
                            required
                            isSearchable
                            isDisabled={providerSelected !== null}
                            id={'providerId'}
                            label="Proveedor"
                            containerClassName='md:col-span-12'
                            value={
                                formik.values.providerId 
                                ? { 
                                    value: formik.values.providerId, 
                                    label: getProvidersList()?.find((g: any) => g.value === formik.values.providerId)?.label 
                                           ?? (headquarter?.provider?.id === formik.values.providerId ? headquarter?.provider?.companyName : '') 
                                  }
                                : null
                            }
                            options={getProvidersList()}
                            onChange={async (e: any) => {
                                formik.setFieldValue('providerId', e?.value);
                                if (e?.value) {
                                    try {
                                        const providerService = new ProviderService();
                                        const response = await providerService.getProvideById(e.value);
                                        const providerData = response.getResponseData();
                                        if (providerData.success && providerData.data) {
                                            if (!formik.values.email) {
                                                formik.setFieldValue('email', providerData.data.email || '');
                                            }
                                        }
                                    } catch (error) {
                                        console.error('Error fetching provider details:', error);
                                    }
                                }
                            }}
                            error={formik.errors.providerId}
                        />

                        {userCan('list', 'route') && (
                            <CustomSelect
                                isSearchable
                                isClearable
                                id={'routeId'}
                                label="Ruta"
                                placeholder="Sin ruta asignada"
                                containerClassName='md:col-span-12'
                                value={
                                    formik.values.routeId
                                        ? {
                                              value: formik.values.routeId,
                                              label:
                                                  getRoutesList()?.find((g: any) => g.value === formik.values.routeId)?.label ??
                                                  headquarter?.routeName ??
                                                  formik.values.routeId,
                                          }
                                        : null
                                }
                                options={getRoutesList()}
                                onChange={(e: any) => formik.setFieldValue('routeId', e?.value ?? '')}
                                error={formik.errors.routeId as string | undefined}
                            />
                        )}

                        <Input
                            id='dayOff'
                            label="Día de descanso"
                            className='md:col-span-12'
                            value={formik.values.dayOff || ''}
                            onChange={(e: any) => formik.setFieldValue('dayOff', e.target.value)}
                            error={formik.errors.dayOff}
                        />

                        <Input
                            id='pickupSchedule'
                            label="Horario de recogida"
                            className='md:col-span-6'
                            value={formik.values.pickupSchedule || ''}
                            onChange={(e: any) => formik.setFieldValue('pickupSchedule', e.target.value)}
                            error={formik.errors.pickupSchedule}
                        />

                        <Input
                            id='schedules'
                            label="Horarios (Apertura/Cierre)"
                            className='md:col-span-6'
                            value={formik.values.schedules || ''}
                            onChange={(e: any) => formik.setFieldValue('schedules', e.target.value)}
                            error={formik.errors.schedules}
                        />

                        <div className="md:col-span-6">
                            <label htmlFor="startService">Inicio de Servicio</label>
                            <DatePicker
                                selected={formik.values.startService}
                                onChange={(date: Date) => formik.setFieldValue('startService', date)}
                                placeholderText="Inicio de Servicio"
                                label="Inicio de Servicio"
                                popperPlacement="bottom-start"
                                popperClassName="!z-[9999] !h-64"
                            />
                        </div>

                        <div className="md:col-span-6">
                            <label htmlFor="endService">Fin de Servicio</label>
                            <DatePicker
                                selected={formik.values.endService}
                                onChange={(date: Date) => formik.setFieldValue('endService', date)}
                                placeholderText="Fin de Servicio"
                                label="Fin de Servicio"
                                popperPlacement="bottom-start"
                                popperClassName="!z-[9999] !h-64"
                            />
                        </div>

                        <Input 
                            id='coordinates'
                            label="Coordenadas"
                            className='md:col-span-12'
                            value={formik.values?.coordinates || ''}
                            onChange={(e: any) => formik.setFieldValue('coordinates', e.target.value)}
                            error={formik.errors.coordinates}
                        />



                        <Input
                            id='contactName'
                            label="Nombre Contacto"
                            className='md:col-span-6'
                            value={formik.values.contactName}
                            onChange={(e: any) => formik.setFieldValue('contactName', e.target.value)}
                            error={formik.errors.contactName}
                        />

                        <Input
                            id='mobilePhone'
                            label="Teléfono Móvil"
                            className='md:col-span-6'
                            value={formik.values.mobilePhone}
                            onChange={(e: any) => formik.setFieldValue('mobilePhone', e.target.value)}
                            error={formik.errors.mobilePhone}
                        />

                        <Input
                            id='phone'
                            label="Teléfono Fijo"
                            className='md:col-span-6'
                            value={formik.values.phone}
                            onChange={(e: any) => formik.setFieldValue('phone', e.target.value)}
                            error={formik.errors.phone}
                        />

                        <Input
                            id='email'
                            label="Email"
                            className='md:col-span-6'
                            value={formik.values.email}
                            onChange={(e: any) => formik.setFieldValue('email', e.target.value)}
                            error={formik.errors.email}
                        />

                        <Input
                            id='gratification'
                            label="Gratificación"
                            className='md:col-span-12'
                            value={formik.values.gratification}
                            onChange={(e: any) => formik.setFieldValue('gratification', e.target.value)}
                            error={formik.errors.gratification}
                        />

                        <Textarea
                            id='comments'
                            label="Observaciones"
                            className='md:col-span-12'
                            value={formik.values.comments}
                            onChange={(e: any) => formik.setFieldValue('comments', e.target.value)}
                            error={formik.errors.comments}
                        />

                        <div className="md:col-span-12 flex items-center gap-2 mt-4">
                            <Switch
                                label="Activo"
                                checked={formik.values.active}
                                onChange={(e: any) => formik.setFieldValue('active', e.target.checked)}
                            />
                        </div>


                        {userCan('list', 'company') && (
                            <CustomSelect
                            required
                            isSearchable
                            id={'company'}
                            label="Compañia"
                            containerClassName='md:col-span-12'
                            value={
                                formik.values.company ?
                                { value: formik.values.company, label: getCompaniesList()?.find((g: any) => g.value === formik.values.company)?.label } :
                                getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU')
                            }
                            options={getCompaniesList()}
                            onChange={
                                (e: any) => { formik.setFieldValue('company', e?.value) }
                            }
                            error={formik.errors.company}
                            />
                        )}

                        <div className='md:col-span-12 flex justify-center'>
                            <Switch
                                id='highSeason'
                                label={'Temporada alta'}
                                checked={formik.values.highSeason}
                                disabled={formik.isSubmitting}
                                onChange={() => formik.setFieldValue('highSeason', !formik.values.highSeason)}
                                switchKnobClassName='bg-secondary'
                            />
                        </div>

                        {formik.values.highSeason && (
                            <div className="md:col-span-12">
                                <div className="bg-gray-50 p-4 rounded-lg border">
                                    <div className="flex justify-between items-center mb-4">
                                        <h3 className="text-lg font-medium text-gray-900">
                                            Fechas de Temporada Alta
                                        </h3>
                                        <Button
                                            type="button"
                                            onClick={addHighSeasonDate}
                                            className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-md text-sm"
                                            disabled={formik.isSubmitting}
                                        >
                                            + Añadir Fecha
                                        </Button>
                                    </div>

                                    {formik.values.highSeasonDates && Array.isArray(formik.values.highSeasonDates) && formik.values.highSeasonDates.length > 0 ? (
                                        <div className="space-y-4">
                                            {formik.values.highSeasonDates.map((date: HighSeasonDate, index: number) => (
                                                <div key={index} className="bg-white p-4 rounded-md border border-gray-200">
                                                    <div className="flex justify-between items-center mb-3">
                                                        <h4 className="text-md font-medium text-gray-700">
                                                            Período {index + 1}
                                                        </h4>
                                                        <Button
                                                            type="button"
                                                            onClick={() => removeHighSeasonDate(index)}
                                                            className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded-md text-sm"
                                                            disabled={formik.isSubmitting}
                                                        >
                                                            × Eliminar
                                                        </Button>
                                                    </div>

                                                    <div className="space-y-4">
                                                        <div className="w-full">
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                Fecha de inicio <span className="text-red-500">*</span>
                                                            </label>
                                                            <DatePicker
                                                                monthsShown={1}
                                                                placeholderText="Selecciona una fecha"
                                                                inputProps={{
                                                                    inputClassName: `h-9 [&_input]:text-ellipsis bg-white w-full ${
                                                                        formik.errors.highSeasonDates && 
                                                                        formik.errors.highSeasonDates[index] && 
                                                                        (formik.errors.highSeasonDates[index] as any)?.dateFrom 
                                                                            ? 'border-red-500' : ''
                                                                    }`,
                                                                }}
                                                                selected={date.dateFrom ? moment(date.dateFrom).toDate() : null}
                                                                onChange={(selectedDate: any) => {
                                                                    const formattedDate = selectedDate ? moment(selectedDate).format('YYYY-MM-DD') : '';
                                                                    updateHighSeasonDate(index, 'dateFrom', formattedDate);
                                                                }}
                                                                locale={es}
                                                            />
                                                            {formik.errors.highSeasonDates && 
                                                             formik.errors.highSeasonDates[index] && 
                                                             (formik.errors.highSeasonDates[index] as any)?.dateFrom && (
                                                                <p className="text-red-500 text-sm mt-1">
                                                                    {(formik.errors.highSeasonDates[index] as any).dateFrom}
                                                                </p>
                                                            )}
                                                        </div>

                                                        <div className="w-full">
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                Fecha de fin <span className="text-red-500">*</span>
                                                            </label>
                                                            <DatePicker
                                                                monthsShown={1}
                                                                placeholderText="Selecciona una fecha"
                                                                inputProps={{
                                                                    inputClassName: `h-9 [&_input]:text-ellipsis bg-white w-full ${
                                                                        formik.errors.highSeasonDates && 
                                                                        formik.errors.highSeasonDates[index] && 
                                                                        (formik.errors.highSeasonDates[index] as any)?.dateTo 
                                                                            ? 'border-red-500' : ''
                                                                    }`,
                                                                }}
                                                                selected={date.dateTo ? moment(date.dateTo).toDate() : null}
                                                                onChange={(selectedDate: any) => {
                                                                    const formattedDate = selectedDate ? moment(selectedDate).format('YYYY-MM-DD') : '';
                                                                    updateHighSeasonDate(index, 'dateTo', formattedDate);
                                                                }}
                                                                locale={es}
                                                            />
                                                            {formik.errors.highSeasonDates && 
                                                             formik.errors.highSeasonDates[index] && 
                                                             (formik.errors.highSeasonDates[index] as any)?.dateTo && (
                                                                <p className="text-red-500 text-sm mt-1">
                                                                    {(formik.errors.highSeasonDates[index] as any).dateTo}
                                                                </p>
                                                            )}
                                                        </div>

                                                        {/* <div className="w-full">
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                                Coordenadas
                                                            </label>
                                                            <Input
                                                                placeholder="(latitud, longitud)"
                                                                value={date.coordinates}
                                                                onChange={(e: any) => updateHighSeasonDate(index, 'coordinates', e.target.value)}
                                                                className={`h-9 w-full ${
                                                                    formik.errors.highSeasonDates && 
                                                                    formik.errors.highSeasonDates[index] && 
                                                                    (formik.errors.highSeasonDates[index] as any)?.coordinates 
                                                                        ? 'border-red-500' : ''
                                                                }`}
                                                            />
                                                            {formik.errors.highSeasonDates && 
                                                             formik.errors.highSeasonDates[index] && 
                                                             (formik.errors.highSeasonDates[index] as any)?.coordinates && (
                                                                <p className="text-red-500 text-sm mt-1">
                                                                    {(formik.errors.highSeasonDates[index] as any).coordinates}
                                                                </p>
                                                            )}
                                                        </div> */}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-8 text-gray-500">
                                            <p>No hay fechas de temporada alta configuradas.</p>
                                            <p className="text-sm">Haz clic en "Añadir Fecha" para comenzar.</p>
                                        </div>
                                    )}

                                    {formik.errors.highSeasonDates && typeof formik.errors.highSeasonDates === 'string' && (
                                        <p className="text-red-500 text-sm mt-2">
                                            {formik.errors.highSeasonDates}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* CAMPOS ANTIGUOS OCULTOS PARA COMPATIBILIDAD CON BACKEND */}
                        <div style={{ display: 'none' }}>
                            {/* Estos campos se mantienen ocultos pero disponibles para compatibilidad */}
                            <div className="md:col-span-6">
                                <label htmlFor="highSeasonDateFrom" className="block text-sm font-medium text-gray-700 mb-2">
                                    Fecha de inicio <span className="text-red-500">*</span>
                                </label>
                                <DatePicker
                                    monthsShown={1}
                                    placeholderText="Selecciona una fecha"
                                    inputProps={{
                                        inputClassName: `h-9 [&_input]:text-ellipsis bg-white ${formik.errors.highSeasonDateFrom ? 'border-red-500' : ''}`,
                                    }}
                                    className="col-span-12"
                                    selected={formik.values?.highSeasonDateFrom ? moment(formik.values.highSeasonDateFrom).toDate() : null}
                                    onChange={(date: any) => {
                                        formik.setFieldValue('highSeasonDateFrom', date);
                                    }}
                                    locale={es}
                                />
                                {formik.errors.highSeasonDateFrom && (
                                    <p className="text-red-500 text-sm">{formik.errors.highSeasonDateFrom?.toString()}</p>
                                )}
                            </div>

                            <div className="md:col-span-6">
                                <label htmlFor="highSeasonDateTo" className="block text-sm font-medium text-gray-700 mb-2">
                                    Fecha de fin <span className="text-red-500">*</span>
                                </label>
                                <DatePicker
                                    monthsShown={1}
                                    placeholderText="Selecciona una fecha"
                                    inputProps={{
                                        inputClassName: `h-9 [&_input]:text-ellipsis bg-white ${formik.errors.highSeasonDateTo ? 'border-red-500' : ''}`,
                                    }}
                                    className="col-span-12"
                                    selected={formik.values?.highSeasonDateTo ? moment(formik.values.highSeasonDateTo).toDate() : null}
                                    onChange={(date: any) => {
                                        formik.setFieldValue('highSeasonDateTo', date);
                                    }}
                                    locale={es}
                                />
                                {formik.errors.highSeasonDateTo && (
                                    <p className="text-red-500 text-sm">{formik.errors.highSeasonDateTo?.toString()}</p>
                                )}
                            </div>

                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}

export default HeadquarterForm;
